import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Button,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack,
  SwapHoriz,
  DeleteOutlined,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useParams, useNavigate } from 'react-router-dom';
import adminService from '../../services/admin.service';
import { useNotification } from '../../context/NotificationContext';
import { getInitials } from '../../utils/getInitials';

interface Instructor {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
}

interface WorkshopStep {
  id: string;
  title: string;
  type: string;
  order: number;
  estimatedTime: number;
}

interface WorkshopSession {
  id: string;
  name: string;
  status: string;
  scheduledStart: string;
  studentCount: number;
  createdAt: string;
}

interface WorkshopDetail {
  id: string;
  title: string;
  description: string;
  status: string;
  difficulty: string;
  category: string;
  estimatedDuration: number;
  maxStudents: number;
  tags: string[];
  prerequisites: string[];
  learningObjectives: string[];
  createdAt: string;
  updatedAt: string;
  instructor: Instructor;
  steps: WorkshopStep[];
  sessions: WorkshopSession[];
  stats: {
    totalStudentsTaught: number;
    averageCompletionRate: number;
    totalSessionsRun: number;
  };
}

const statusColors: Record<string, 'default' | 'success' | 'warning'> = {
  draft: 'default',
  published: 'success',
  archived: 'warning',
};

const sessionStatusColors: Record<string, 'info' | 'success' | 'warning' | 'default' | 'error'> = {
  scheduled: 'info',
  active: 'success',
  paused: 'warning',
  completed: 'default',
  cancelled: 'error',
};

const AdminWorkshopDetail: React.FC = () => {
  const { showNotification } = useNotification();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [workshop, setWorkshop] = useState<WorkshopDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIndex, setTabIndex] = useState(0);

  // Transfer dialog state
  const [transferOpen, setTransferOpen] = useState(false);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [selectedInstructor, setSelectedInstructor] = useState<Instructor | null>(null);
  const [transferLoading, setTransferLoading] = useState(false);

  // Delete dialog state
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const fetchWorkshop = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await adminService.getWorkshop(id);
      setWorkshop(res.data);
    } catch {
      showNotification('Failed to load tech eval details', 'error');
      setError('Failed to load workshop');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchWorkshop();
  }, [fetchWorkshop]);

  const handleStatusChange = async (newStatus: string) => {
    if (!workshop) return;
    try {
      await adminService.updateWorkshopStatus(workshop.id, newStatus);
      fetchWorkshop();
    } catch {
      showNotification('Failed to update tech eval status', 'error');
    }
  };

  const handleOpenTransfer = async () => {
    setSelectedInstructor(null);
    setTransferOpen(true);
    try {
      const res = await adminService.getInstructors();
      setInstructors(res.data);
    } catch {
      showNotification('Failed to load instructors', 'error');
    }
  };

  const handleTransfer = async () => {
    if (!workshop || !selectedInstructor) return;
    setTransferLoading(true);
    try {
      await adminService.transferWorkshop(workshop.id, selectedInstructor.id);
      setTransferOpen(false);
      fetchWorkshop();
    } catch {
      showNotification('Failed to transfer tech eval', 'error');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!workshop) return;
    setDeleteError('');
    setDeleteLoading(true);
    try {
      await adminService.deleteWorkshop(workshop.id);
      navigate('/admin/workshops');
    } catch (err: any) {
      setDeleteError(err.response?.data?.message || 'Failed to delete workshop');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !workshop) {
    return <Alert severity="error">{error || 'Tech Eval not found'}</Alert>;
  }

  const instructorInitials = getInitials(workshop.instructor.firstName, workshop.instructor.lastName);

  return (
    <Box>
      {/* Back button */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate('/admin/workshops')}
        sx={{ mb: 2, textTransform: 'none' }}
      >
        Back to Workshops
      </Button>

      {/* Top section: Workshop info + Actions */}
      <Paper sx={{ p: 3, borderRadius: '12px', mb: 3 }} elevation={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          {/* Left: Workshop info */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {workshop.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {workshop.description}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label={workshop.status}
                size="small"
                color={statusColors[workshop.status] || 'default'}
                sx={{ textTransform: 'capitalize', fontWeight: 500 }}
              />
              <Chip label={workshop.difficulty} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
              <Chip label={workshop.category} size="small" variant="outlined" />
            </Box>
          </Box>

          {/* Right: Actions */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapHoriz />}
              onClick={handleOpenTransfer}
              sx={{ textTransform: 'none' }}
            >
              Transfer Ownership
            </Button>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={workshop.status}
                label="Status"
                onChange={(e) => handleStatusChange(e.target.value)}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<DeleteOutlined />}
              onClick={() => {
                setDeleteConfirm('');
                setDeleteError('');
                setDeleteOpen(true);
              }}
              sx={{ textTransform: 'none' }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Stats row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', bgcolor: 'background.default', textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#3b82f6' }}>
              {workshop.stats.totalStudentsTaught}
            </Typography>
            <Typography variant="caption" color="text.secondary">Total Students Taught</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', bgcolor: 'background.default', textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#22c55e' }}>
              {workshop.stats.averageCompletionRate}%
            </Typography>
            <Typography variant="caption" color="text.secondary">Avg Completion Rate</Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: '8px', bgcolor: 'background.default', textAlign: 'center' }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
              {workshop.stats.totalSessionsRun}
            </Typography>
            <Typography variant="caption" color="text.secondary">Total Sessions Run</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: '12px' }} elevation={0}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => setTabIndex(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          <Tab label="Overview" sx={{ textTransform: 'none' }} />
          <Tab label="Steps" sx={{ textTransform: 'none' }} />
          <Tab label="Sessions" sx={{ textTransform: 'none' }} />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* Tab 0: Overview */}
          {tabIndex === 0 && (
            <Grid container spacing={3}>
              {/* Left: Tech Eval details */}
              <Grid size={{ xs: 12, md: 7 }}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Tech Eval Details
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Estimated Duration</Typography>
                      <Typography variant="body2">{workshop.estimatedDuration} minutes</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Max Students</Typography>
                      <Typography variant="body2">{workshop.maxStudents}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Tags</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        {workshop.tags.length > 0 ? (
                          workshop.tags.map((tag) => (
                            <Chip key={tag} label={tag} size="small" variant="outlined" />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">None</Typography>
                        )}
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Prerequisites</Typography>
                      {workshop.prerequisites.length > 0 ? (
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                          {workshop.prerequisites.map((p, i) => (
                            <li key={i}><Typography variant="body2">{p}</Typography></li>
                          ))}
                        </ul>
                      ) : (
                        <Typography variant="body2" color="text.secondary">None</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Learning Objectives</Typography>
                      {workshop.learningObjectives.length > 0 ? (
                        <ul style={{ margin: '4px 0 0 0', paddingLeft: 20 }}>
                          {workshop.learningObjectives.map((obj, i) => (
                            <li key={i}><Typography variant="body2">{obj}</Typography></li>
                          ))}
                        </ul>
                      ) : (
                        <Typography variant="body2" color="text.secondary">None</Typography>
                      )}
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Created</Typography>
                      <Typography variant="body2">{dayjs(workshop.createdAt).format('MMM D, YYYY')} ({dayjs(workshop.createdAt).fromNow()})</Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Last Updated</Typography>
                      <Typography variant="body2">{dayjs(workshop.updatedAt).format('MMM D, YYYY')} ({dayjs(workshop.updatedAt).fromNow()})</Typography>
                    </Box>
                  </Box>
                </Paper>
              </Grid>

              {/* Right: Instructor card */}
              <Grid size={{ xs: 12, md: 5 }}>
                <Paper variant="outlined" sx={{ p: 3, borderRadius: '12px', textAlign: 'center' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Instructor
                  </Typography>
                  <Avatar
                    sx={{
                      width: 64,
                      height: 64,
                      bgcolor: '#8b5cf6',
                      fontSize: '1.4rem',
                      fontWeight: 600,
                      mx: 'auto',
                      mb: 1.5,
                    }}
                  >
                    {instructorInitials}
                  </Avatar>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {workshop.instructor.firstName} {workshop.instructor.lastName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {workshop.instructor.email}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: 'none' }}
                    onClick={() => navigate(`/admin/users/${workshop.instructor.id}`)}
                  >
                    View Profile
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Steps */}
          {tabIndex === 1 && (
            <>
              {workshop.steps.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No steps defined for this tech eval.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Title</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Estimated Time</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...workshop.steps]
                        .sort((a, b) => a.order - b.order)
                        .map((step) => (
                          <TableRow key={step.id}>
                            <TableCell>{step.order}</TableCell>
                            <TableCell>{step.title}</TableCell>
                            <TableCell>
                              <Chip label={step.type} size="small" variant="outlined" sx={{ textTransform: 'capitalize' }} />
                            </TableCell>
                            <TableCell>{step.estimatedTime} min</TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}

          {/* Tab 2: Sessions */}
          {tabIndex === 2 && (
            <>
              {workshop.sessions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                  No sessions have been created for this tech eval.
                </Typography>
              ) : (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Scheduled Start</TableCell>
                        <TableCell>Students</TableCell>
                        <TableCell>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {workshop.sessions.map((session) => (
                        <TableRow
                          key={session.id}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/admin/sessions/${session.id}`)}
                        >
                          <TableCell>{session.name}</TableCell>
                          <TableCell>
                            <Chip
                              label={session.status}
                              size="small"
                              color={sessionStatusColors[session.status] || 'default'}
                              sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell>{dayjs(session.scheduledStart).format('MMM D, YYYY h:mm A')}</TableCell>
                          <TableCell>{session.studentCount}</TableCell>
                          <TableCell>{dayjs(session.createdAt).fromNow()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Transfer Ownership Dialog */}
      <Dialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>Transfer Ownership</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <Autocomplete
            options={instructors.filter((i) => i.id !== workshop.instructor.id)}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
            value={selectedInstructor}
            onChange={(_, value) => setSelectedInstructor(value)}
            renderInput={(params) => (
              <TextField {...params} label="Select New Instructor" size="small" />
            )}
          />
          {selectedInstructor && (
            <Typography variant="body2">
              Current: <strong>{workshop.instructor.firstName} {workshop.instructor.lastName}</strong> &rarr; New: <strong>{selectedInstructor.firstName} {selectedInstructor.lastName}</strong>
            </Typography>
          )}
          <Alert severity="warning">
            This will transfer all sessions for this tech eval.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setTransferOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTransfer}
            disabled={transferLoading || !selectedInstructor}
          >
            {transferLoading ? <CircularProgress size={20} /> : 'Transfer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Tech Eval Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px' } }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: 'error.main' }}>Delete Tech Eval</DialogTitle>
        <DialogContent>
          {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
          <Typography variant="body2" sx={{ mb: 2 }}>
            This will permanently delete <strong>{workshop.title}</strong> and all associated sessions, steps, and student progress. This action cannot be undone.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Type <strong>{workshop.title}</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={deleteConfirm}
            onChange={(e) => setDeleteConfirm(e.target.value)}
            placeholder={workshop.title}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading || deleteConfirm !== workshop.title}
          >
            {deleteLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminWorkshopDetail;
